import { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiDownload, FiEye, FiMail, FiPhone, FiCalendar, FiCheckCircle, FiXCircle, FiBookOpen } from 'react-icons/fi';
import { studentService } from '../services/apiService';
import * as XLSX from 'xlsx';

const StudentsList = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    totalItems: 0
  });

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      if (filterVerified === 'verified') {
        params.isVerified = true;
      } else if (filterVerified === 'unverified') {
        params.isVerified = false;
      }

      const response = await studentService.getAllStudents(params);
      
      if (response && response.data) {
        setStudents(response.data);
        if (response.pagination) {
          setPagination({
            current: response.pagination.current,
            total: response.pagination.total,
            totalItems: response.pagination.totalItems
          });
        }
      }
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVerified, pagination.current]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchStudents();
      return;
    }

    try {
      setLoading(true);
      const response = await studentService.getAllStudents({
        search: searchTerm,
        page: 1,
        limit: 20
      });
      
      if (response && response.data) {
        setStudents(response.data);
        if (response.pagination) {
          setPagination({
            current: response.pagination.current,
            total: response.pagination.total,
            totalItems: response.pagination.totalItems
          });
        }
      }
    } catch (err) {
      console.error('Error searching students:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await studentService.getAllStudents({
        limit: 10000, // Get all students
        isVerified: true
      });
      
      const allStudents = response.data || [];
      
      if (allStudents.length === 0) {
        alert('No students to export');
        return;
      }

      const exportData = allStudents.map(student => ({
        'Name': student.name || '',
        'Email': student.email || '',
        'Mobile': student.mobileNumber || '',
        'Institute': student.instituteName || '',
        'Course': student.courseName || '',
        'Year': student.year || '',
        'Branch': student.branch || '',
        'Roll Number': student.rollNumber || '',
        'City': student.city || '',
        'State': student.state || '',
        'Country': student.country || '',
        'Verified': student.isVerified ? 'Yes' : 'No',
        'Registered On': new Date(student.createdAt).toLocaleDateString()
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Students');
      XLSX.writeFile(wb, `students_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error downloading students:', err);
      alert('Failed to download students list');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current: newPage }));
  };

  const filteredStudents = students.filter(student => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        student.name?.toLowerCase().includes(search) ||
        student.email?.toLowerCase().includes(search) ||
        student.instituteName?.toLowerCase().includes(search) ||
        student.rollNumber?.toLowerCase().includes(search) ||
        student.city?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Youth Ambassadors</h1>
        <p className="mt-1 text-sm text-gray-600">
          Total: {pagination.totalItems} youth ambassadors
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, email, institute, roll number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
              />
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          {/* Verification Filter */}
          <div>
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            >
              <option value="all">All Youth Ambassadors</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center"
          >
            <FiSearch className="mr-2" />
            Search
          </button>
          <button
            onClick={downloadExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <FiDownload className="mr-2" />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#166534]"></div>
            <p className="mt-2 text-gray-600">Loading students...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No students found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institute
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.rollNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.email}</div>
                        <div className="text-sm text-gray-500">{student.mobileNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.instituteName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.courseName}</div>
                        <div className="text-sm text-gray-500">
                          {student.year && `Year ${student.year}`}
                          {student.branch && ` • ${student.branch}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.city}</div>
                        <div className="text-sm text-gray-500">{student.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.isVerified ? (
                          <span className="flex items-center text-green-600">
                            <FiCheckCircle className="mr-1" />
                            <span className="text-sm">Yes</span>
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-600">
                            <FiXCircle className="mr-1" />
                            <span className="text-sm">No</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setSelectedStudent(student)}
                          className="text-[#166534] hover:text-[#0e3d1f] flex items-center"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(pagination.current - 1) * 20 + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(pagination.current * 20, pagination.totalItems)}</span> of{' '}
                      <span className="font-medium">{pagination.totalItems}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.current - 1)}
                        disabled={pagination.current === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {[...Array(Math.min(pagination.total, 5))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pagination.current === pageNum
                                ? 'z-10 bg-[#166534] border-[#166534] text-white'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(pagination.current + 1)}
                        disabled={pagination.current === pagination.total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-semibold text-gray-900">Student Details</h2>
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="text-sm font-medium">{selectedStudent.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiMail className="mr-1" />
                        {selectedStudent.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mobile</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiPhone className="mr-1" />
                        {selectedStudent.mobileNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="text-sm font-medium">
                        {selectedStudent.dateOfBirth 
                          ? new Date(selectedStudent.dateOfBirth).toLocaleDateString()
                          : 'Not provided'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Academic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Institute</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiBookOpen className="mr-1" />
                        {selectedStudent.instituteName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Course</p>
                      <p className="text-sm font-medium">{selectedStudent.courseName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Year</p>
                      <p className="text-sm font-medium">{selectedStudent.year || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Branch</p>
                      <p className="text-sm font-medium">{selectedStudent.branch || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Roll Number</p>
                      <p className="text-sm font-medium">{selectedStudent.rollNumber || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">University</p>
                      <p className="text-sm font-medium">{selectedStudent.university || 'Not provided'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Location</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">City</p>
                      <p className="text-sm font-medium">{selectedStudent.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">State</p>
                      <p className="text-sm font-medium">{selectedStudent.state}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Country</p>
                      <p className="text-sm font-medium">{selectedStudent.country}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Zip Code</p>
                      <p className="text-sm font-medium">{selectedStudent.zipCode || 'Not provided'}</p>
                    </div>
                  </div>
                  {selectedStudent.addressLine1 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="text-sm font-medium">{selectedStudent.addressLine1}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Additional Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Verified</p>
                      <p className="text-sm font-medium">{selectedStudent.isVerified ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Registered On</p>
                      <p className="text-sm font-medium flex items-center">
                        <FiCalendar className="mr-1" />
                        {new Date(selectedStudent.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsList;
